import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import Avatar, { getMentor } from "./avatars/AvatarLibrary";
import { Mic, MessageSquare, Bell, BellOff, Sparkles, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { resolveFirstName } from "../lib/firstName";

const TIME_GREETINGS = [
  { until: 11, msg: (n) => `Good morning, ${n}.` },
  { until: 17, msg: (n) => `Hey ${n}.` },
  { until: 22, msg: (n) => `Evening, ${n}.` },
  { until: 24, msg: (n) => `Late night, ${n}?` },
];

const PILLAR_PROMPTS = {
  financial: "Let's look at one money move that gives you breathing room.",
  health: "Let's lock in one body or sleep move for today.",
  spirituality: "Let's settle the mind first — even five minutes counts.",
  relationships: "Let's pick one connection to nurture today.",
  endgame: "Let's reconnect with the bigger picture — one step toward it.",
};

export default function AvatarGreetingCard() {
  const { user, onboarding } = useAuth();
  const navigate = useNavigate();
  const [todayLine, setTodayLine] = useState("Loading today's focus…");
  const [pushState, setPushState] = useState("unsupported"); // unsupported | denied | default | granted
  const [billing, setBilling] = useState(null);
  const mentor = getMentor(onboarding?.mentor_id);
  const firstName = onboarding?.preferred_name || resolveFirstName(user, "friend");

  const greeting =
    TIME_GREETINGS.find((g) => new Date().getHours() < g.until)?.msg(firstName) || `Hi ${firstName}.`;

  // Pull today's focus from the roadmap doc; fall back to a pillar-aware
  // prompt so the card never says something generic when onboarding has data.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/roadmap`);
        const c = r.data?.content || {};
        if (c.first_next_step) {
          setTodayLine(c.first_next_step);
          return;
        }
        // No roadmap step yet → use onboarding to make this feel personal.
        const focusPillar = onboarding?.focus?.area || onboarding?.focus;
        if (typeof focusPillar === "string" && PILLAR_PROMPTS[focusPillar]) {
          setTodayLine(PILLAR_PROMPTS[focusPillar]);
        } else if (c.endgame_vision) {
          setTodayLine(`Stay close to: "${c.endgame_vision}"`);
        } else {
          setTodayLine(`One small step is enough today. What would move you forward?`);
        }
      } catch (e) {
        setTodayLine(`One small step is enough today. What would move you forward?`);
      }
    })();
  }, [onboarding]);

  // Fetch billing once to surface trial countdown + upgrade nudge.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/billing/me`);
        setBilling(r.data || null);
      } catch (e) {}
    })();
  }, []);

  // Detect push notification capability
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    setPushState(Notification.permission);
  }, []);

  const enablePush = async () => {
    try {
      track("push_enable_click");
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPushState(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked. Enable them in browser settings.");
        return;
      }
      // Fetch VAPID public key from backend
      const k = await axios.get(`${API}/push/public-key`);
      const vapid = k.data?.public_key;
      if (!vapid) {
        toast.error("Push not configured on the server yet.");
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
      toast.success("Got it. I'll check in on you.");
      track("push_subscribed");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't enable notifications. Try again.");
    }
  };

  // Quick check-in: open the existing /coach with a prefilled opener so the
  // user feels the mentor lead the conversation, not the other way around.
  const startCheckIn = () => {
    sessionStorage.setItem(
      "ascend_coach_prefill",
      "Quick check-in — what's the one thing on my mind right now and what should I do about it today?"
    );
    track("greeting_checkin_click");
    navigate("/coach");
  };

  // Trial countdown + upgrade nudge
  const sub = billing?.subscription || {};
  const isTrial = sub.is_trial;
  let trialDaysLeft = null;
  if (isTrial && sub.expires_at) {
    const ms = new Date(sub.expires_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }

  return (
    <section
      data-testid="avatar-greeting-card"
      className="rounded-3xl bg-gradient-to-br from-[#0f1724] via-[#162237] to-[#1c2944] text-white p-6 md:p-8 mb-6 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(72,106,143,0.5) 0%, transparent 45%), radial-gradient(circle at 85% 70%, rgba(229,214,178,0.3) 0%, transparent 45%)",
        }}
      />
      <div className="relative flex items-start gap-5">
        <div className="shrink-0">
          <Avatar id={mentor.id} size={92} state="idle" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <p className="font-mono-eyebrow text-[#8aa7c4]">{mentor.label.toUpperCase()} · YOUR MENTOR</p>
            {isTrial && trialDaysLeft !== null && (
              <span
                data-testid="trial-countdown-pill"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#c18848]/20 text-[#e9c896] text-[10px] font-mono-eyebrow"
              >
                TRIAL · {trialDaysLeft} {trialDaysLeft === 1 ? "DAY" : "DAYS"} LEFT
              </span>
            )}
          </div>
          <p className="font-display text-[24px] md:text-[28px] tracking-tight leading-tight">
            {greeting}
          </p>
          <p className="font-body text-[14px] md:text-[15px] text-white/80 mt-2 leading-relaxed">
            {todayLine}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              data-testid="greeting-checkin-btn"
              onClick={startCheckIn}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white text-[13px] font-medium"
            >
              <Sparkles size={13} /> Today's check-in
            </button>
            <button
              data-testid="greeting-voice-call-btn"
              onClick={() => {
                track("greeting_voice_call_click");
                navigate("/coach/voice");
              }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white text-[13px] font-medium"
            >
              <Mic size={14} /> Talk to me
            </button>
            <button
              data-testid="greeting-text-coach-btn"
              onClick={() => navigate("/coach")}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[13px] font-medium"
            >
              <MessageSquare size={13} /> Message
            </button>
            {isTrial && trialDaysLeft !== null && trialDaysLeft <= 2 && (
              <button
                data-testid="greeting-upgrade-btn"
                onClick={() => {
                  track("greeting_upgrade_click");
                  navigate("/pricing");
                }}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#c18848] hover:bg-[#a8743a] text-white text-[13px] font-medium"
              >
                <ArrowUpRight size={14} /> Keep my mentor
              </button>
            )}
            {pushState === "default" && (
              <button
                data-testid="greeting-enable-push-btn"
                onClick={enablePush}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[13px] font-medium"
              >
                <Bell size={13} /> Let me check in on you
              </button>
            )}
            {pushState === "denied" && (
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/5 border border-white/10 text-white/60 text-[12px]">
                <BellOff size={12} /> Notifications blocked
              </span>
            )}
            {pushState === "granted" && (
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/10 border border-white/15 text-white/80 text-[12px]">
                <Bell size={12} /> Check-ins on
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// Helper: VAPID b64url → Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}
